import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { formatRiskRecognitionCardDisplay, getRiskRecognitionIcon } from './format';
import { toDriverDetails } from './mockInvestigation';
import { isKnownRiskRule } from './parse';
import { renderRiskHighlight } from './RiskHighlightText';

interface RiskFootageInfoProps {
  highlight: RiskRecognitionHighlight;
}

/**
 * Contexto compacto do vídeo em 2 colunas: à esquerda o evento (ícone + nome + descrição), à direita
 * motorista/veículo/local. Ajuda a investigação a não confundir a qual caso o clipe pertence. Rótulos dos
 * metadados ficam em `sr-only` (o ícone já os identifica); campos secundários ausentes são omitidos. Em
 * telas estreitas as colunas empilham.
 */
const RiskFootageInfo = ({ highlight }: RiskFootageInfoProps) => {
  const { t } = useTranslation('notifications');
  const details = toDriverDetails(highlight);
  const ruleName = highlight.metadata?.rule_name;
  const eventName = isKnownRiskRule(ruleName) ? t(`feed.rule.${ruleName}`) : t('feed.rule.unknown');
  const eventSummary = formatRiskRecognitionCardDisplay(highlight).summary?.trim() ?? '';

  const allRows: { key: string; icon: IconNames; label: string; value: string }[] = [
    { key: 'driver', icon: 'square-user', label: t('feed.footage.info.driver'), value: details.driverName },
    { key: 'vehicle', icon: 'truck', label: t('feed.footage.info.vehicle'), value: details.vehicleLabel },
    { key: 'location', icon: 'map-pin', label: t('feed.footage.info.location'), value: details.locationAddress },
  ];
  const rows = allRows.filter((row) => row.value.trim().length > 0);

  return (
    <div className='grid grid-cols-1 items-start gap-x-4 gap-y-2 rounded-lg bg-gray-2 px-3 py-2.5 sm:grid-cols-2'>
      <div className='flex min-w-0 flex-col gap-0.5'>
        <div className='flex items-center gap-2'>
          <Icon name={getRiskRecognitionIcon(highlight)} size='sm' className='shrink-0 text-[#71717a]' aria-hidden />
          <Typography as='span' variant='text-sm-medium' className='min-w-0 text-[#1c2024]'>
            {eventName}
          </Typography>
        </div>
        {eventSummary ? (
          <Typography as='p' variant='text-sm' className='text-[#3f3f46] leading-snug [overflow-wrap:anywhere]'>
            {renderRiskHighlight(eventSummary)}
          </Typography>
        ) : null}
      </div>

      {rows.length > 0 ? (
        <dl className='flex min-w-0 flex-col gap-1'>
          {rows.map((row) => (
            <div key={row.key} className='flex min-w-0 items-center gap-2'>
              <Icon name={row.icon} size='sm' className='shrink-0 text-[#71717a]' aria-hidden />
              <dt className='sr-only'>{row.label}</dt>
              <dd className='min-w-0'>
                <Typography as='span' variant='text-sm' className='text-[#3f3f46] [overflow-wrap:anywhere]'>
                  {row.value}
                </Typography>
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
};

export default RiskFootageInfo;
